<template>
  <div class="security-audit">
    <h3>安全审计</h3>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="登录记录" name="logs">
        <el-form :inline="true" size="small" class="filter-form">
          <el-form-item label="IP">
            <el-input v-model="filters.ip" clearable placeholder="模糊搜索" @keyup.enter.native="onSearch" />
          </el-form-item>
          <el-form-item label="结果">
            <el-select v-model="filters.success" clearable placeholder="全部">
              <el-option label="成功" :value="true" />
              <el-option label="失败" :value="false" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="onSearch">查询</el-button>
            <el-button @click="onReset">重置</el-button>
          </el-form-item>
        </el-form>
        <el-table v-loading="logLoading" :data="loginLogs" stripe>
          <el-table-column label="时间" width="170">
            <template slot-scope="{ row }">{{ formatDateTime(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column prop="ipAddress" label="IP" width="140" />
          <el-table-column label="结果" width="90">
            <template slot-scope="{ row }">
              <el-tag :type="row.success ? 'success' : 'danger'" size="mini">
                {{ row.success ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="failureReason" label="说明" width="120" />
          <el-table-column prop="userAgent" label="User-Agent" min-width="220" show-overflow-tooltip />
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" @click="blockFromLog(row)">封禁 IP</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pager"
          layout="total, prev, pager, next"
          :current-page="logPage + 1"
          :page-size="logSize"
          :total="logTotal"
          @current-change="onLogPageChange"
        />
      </el-tab-pane>

      <el-tab-pane label="IP 封禁" name="blocks">
        <div class="toolbar">
          <el-form :inline="true" size="small" @submit.native.prevent>
            <el-form-item label="IP">
              <el-input v-model="blockForm.ipAddress" placeholder="如 203.0.113.10" />
            </el-form-item>
            <el-form-item label="原因">
              <el-input v-model="blockForm.reason" placeholder="可选" />
            </el-form-item>
            <el-form-item>
              <el-button type="danger" @click="addBlock">添加封禁</el-button>
            </el-form-item>
          </el-form>
        </div>
        <el-table v-loading="blockLoading" :data="ipBlocks" stripe>
          <el-table-column prop="ipAddress" label="IP" width="160" />
          <el-table-column prop="reason" label="原因" min-width="180" />
          <el-table-column label="封禁时间" width="170">
            <template slot-scope="{ row }">{{ formatDateTime(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="{ row }">
              <el-button type="text" @click="removeBlock(row)">解封</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import {
  fetchLoginLogs,
  fetchIpBlocks,
  blockIp,
  unblockIp,
} from '../api/admin';
import { formatDateTime } from '../constants/planLabels';

export default {
  name: 'SecurityAudit',
  data() {
    return {
      activeTab: 'logs',
      logLoading: false,
      blockLoading: false,
      loginLogs: [],
      ipBlocks: [],
      logPage: 0,
      logSize: 20,
      logTotal: 0,
      filters: { ip: '', success: null },
      blockForm: { ipAddress: '', reason: '' },
    };
  },
  mounted() {
    this.loadLoginLogs();
    this.loadIpBlocks();
  },
  methods: {
    formatDateTime,
    async loadLoginLogs() {
      this.logLoading = true;
      try {
        const data = await fetchLoginLogs({
          page: this.logPage,
          size: this.logSize,
          ip: this.filters.ip || undefined,
          success: this.filters.success,
        });
        this.loginLogs = data.items || [];
        this.logTotal = data.total || 0;
      } catch (e) {
        this.$message.error(e.message || '加载失败');
      } finally {
        this.logLoading = false;
      }
    },
    async loadIpBlocks() {
      this.blockLoading = true;
      try {
        this.ipBlocks = await fetchIpBlocks();
      } catch (e) {
        this.$message.error(e.message || '加载失败');
      } finally {
        this.blockLoading = false;
      }
    },
    onSearch() {
      this.logPage = 0;
      this.loadLoginLogs();
    },
    onReset() {
      this.filters = { ip: '', success: null };
      this.logPage = 0;
      this.loadLoginLogs();
    },
    onLogPageChange(p) {
      this.logPage = p - 1;
      this.loadLoginLogs();
    },
    async blockFromLog(row) {
      this.blockForm.ipAddress = row.ipAddress;
      this.blockForm.reason = row.success ? '手动封禁' : `登录失败: ${row.failureReason || '未知'}`;
      this.activeTab = 'blocks';
      await this.addBlock();
    },
    async addBlock() {
      if (!this.blockForm.ipAddress) {
        this.$message.warning('请输入 IP');
        return;
      }
      try {
        await this.$confirm(`确认封禁 IP ${this.blockForm.ipAddress}？`, '确认', { type: 'warning' });
        await blockIp({
          ipAddress: this.blockForm.ipAddress,
          reason: this.blockForm.reason || undefined,
        });
        this.$message.success('已封禁');
        this.blockForm = { ipAddress: '', reason: '' };
        this.loadIpBlocks();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.message || '封禁失败');
        }
      }
    },
    async removeBlock(row) {
      try {
        await this.$confirm(`确认解封 IP ${row.ipAddress}？`, '确认', { type: 'warning' });
        await unblockIp(row.ipAddress);
        this.$message.success('已解封');
        this.loadIpBlocks();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.message || '解封失败');
        }
      }
    },
  },
};
</script>

<style scoped>
.filter-form { margin-bottom: 12px; }
.toolbar { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
</style>
